import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons"
import { ButtonProps, Checkbox, Col, Select, Space } from "antd"
import React from "react"
import { useNavigate } from "react-router"

import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons } from "components/Card/Card"
import { ITEMS_PER_CAROUSEL_PAGE } from "containers/Survey/models"
import { ACTION, Reply, ReplyAction } from "containers/Survey/types"

import styles from "./styles.module.scss"
import {
  UiArrowProps,
  UiButtonProps,
  UiCheckboxesProps,
  UiCheckboxProps,
  UiFooterProps,
  UiGenericSlideProps,
  UiInlineProps,
  UiSelectorProps,
  UiSlideProps,
} from "./types"
import { useLanguage } from "locales"
import { isValueSelected } from "containers/Survey/utils"

export const UiGenericSlide: React.FC<UiGenericSlideProps> = (props) => {
  const items = [...props.items]

  return (
    <Space direction="horizontal" className={styles.ButtonSpacer} size={30}>
      {items.map((item) => item)}
    </Space>
  )
}

export const UiSlide = (props: UiSlideProps): JSX.Element => {
  const length = props.items.length
  const items = [...props.items]
  const addMore =
    length >= ITEMS_PER_CAROUSEL_PAGE / 2 && length < ITEMS_PER_CAROUSEL_PAGE
      ? ITEMS_PER_CAROUSEL_PAGE - length
      : 0
  if (addMore && !props.preventAddMore && props.variant !== 'small')
    Array.from(Array(addMore)).forEach(() =>
      items.push({ title: "", value: "", action: ACTION.nothing } as Reply),
    )
  const gap = length === 2 ? 90 : 30
  return (
    <Space direction="horizontal" className={styles.ButtonSpacer} size={gap}>
      {items.map((item, i) => {
        const selected = isValueSelected(props.selected, item.value)
        return item.ui?.view === "button" ? (
          <UiButton
            data-testid={`test-ui-button-${item.title}`}
            key={i}
            item={item}
            disabled={item.action === ACTION.nothing}
            onClick={() => {
              selected ? props.unSelect(item) : props.onSelect(item)
            }}
          />
        ) : (
          <UiFatButton
            size={props.variant}
            data-testid={`test-ui-fat-button-${item.title}`}
            key={i}
            disabled={item.action === ACTION.nothing}
            item={item}
            onClick={() => {
              selected ? props.unSelect(item) : props.onSelect(item)
            }}
            selected={selected}
          />
        )
      })}
    </Space>
  )
}

export const UiCheckboxes: React.FC<UiCheckboxesProps> = (
  props,
): JSX.Element => {
  return (
    <Space
      direction="vertical"
      size={0}
      style={{
        width: ` ${100 / ITEMS_PER_CAROUSEL_PAGE}%`,
        textAlign: "left",
      }}
    >
      {props.items.map((item, i) => {
        const selected = isValueSelected(props.selected, item.value)
        return (
          <UiCheckbox
            key={i}
            item={item}
            onClick={() => {
              selected ? props.unSelect(item) : props.onSelect(item)
            }}
            selected={selected}
          />
        )
      })}
    </Space>
  )
}
const AdditionalActions: React.FC<ReplyAction & { onClick: () => void }> = (
  props,
): JSX.Element => {
  const navigate = useNavigate()

  const handleClick = () => {
    if (props.action === ACTION.redirect) {
      navigate(props.value as string)
    }
  }
  return props.action === ACTION.nothing ? (
    <span className="font-size-small">{props.title}</span>
  ) : (
    <Button type="link" onClick={handleClick}>
      {props?.ui?.view === "icon" ? (
        props.icon
      ) : (
        <span className="font-size-small">{props.title}</span>
      )}
    </Button>
  )
}

export const UiFatButton: React.FC<UiButtonProps> = (props): JSX.Element => {
  return (
    <div style={{ position: "relative" }}>
      <FatButton
        onClick={() => props.onClick(props.item)}
        clicked={props.selected}
        type={props.item.ui?.type}
        img={props.item.imgUrl}
        disabled={props.disabled}
        doNotFormat={true}
        style={props.size === 'small' ? { fontSize: '20px', height: '220px' } : undefined}
        size={props.size}
        additionalMentions={props?.item?.subtitle && <div>
            <span style={{fontSize: '16px', color: props?.disabled ? '#7A7A7A' : props?.item?.ui?.type === 'primary' ? '#fff' : '#27224F', fontWeight: 'bold' }}>{props.item.subtitle}</span>
          </div>}
      >
          {props.item.title}
      </FatButton>
      {props.item.additionalActions && (
        <div className={styles.AdditionalActionsWrapper}>
          {props.item.additionalActions.map((additionalAction, key) => (
            <AdditionalActions
              onClick={() => props.onClick(additionalAction)}
              key={key}
              {...additionalAction}
            />
          ))}
        </div>
      )}
    </div>
  )
}


export const UiButton: React.FC<
  Omit<ButtonProps, "onClick"> & { item: Reply; onClick: (item: Reply) => void }
> = (props): JSX.Element => {
  return (
    <Button
      data-testid="ui-button"
      wide="long"
      onClick={() => props.onClick(props.item)}
      type={props.item?.ui?.type}
      disabled={props.disabled}
    >
      {props.item.title}
    </Button>
  )
}
export const UiCheckbox: React.FC<UiCheckboxProps> = (props): JSX.Element => {
  return (
    <Checkbox
      style={{ width: "100%" }}
      checked={props.selected}
      onClick={() => props.onClick(props.item)}
    >
      {props.item.title}
    </Checkbox>
  )
}

export const UiArrow: React.FC<UiArrowProps> = (props): JSX.Element => {
  return (
    <Col
      className={`${styles.AlignMiddle} ${
        props.side === "left" ? styles.AlignRight : styles.AlignLeft
      }`}
      span={2}
    >
      {props.side === "left" ? (
        <CaretLeftOutlined
          data-testid={`caret-left`}
          className={styles.CarouselArrow}
          onClick={props.onClick}
          hidden={props.hidden}
        />
      ) : (
        <CaretRightOutlined
          data-testid={`caret-right`}
          className={styles.CarouselArrow}
          onClick={props.onClick}
          hidden={props.hidden}
        />
      )}
    </Col>
  )
}

export const UiSelector = (props: UiSelectorProps): JSX.Element => {
  const { languages } = useLanguage()
  return (
    <Select
      style={{ width: "100%" }}
      placeholder={languages.select_reason}
      defaultValue={props.selectedValue as string | undefined}
      onChange={(v: string) =>
        props.onSelect(props.items.find((item) => item.value === v) as Reply)
      }
    >
      {props.items.map((item: Reply, key: number) => (
        <Select.Option key={key} value={item.value as string}>
          {item.title}
        </Select.Option>
      ))}
    </Select>
  )
}

export const UiFooter: React.FC<UiFooterProps> = (props) => {
  const { languages } = useLanguage()
  return (
    <Buttons style={props.style}>
      {props.childrenFirst ? props.children : null}
      {
        props.allowSkip && (
          <Button type="link" onClick={props.onFinish}>
            {languages.skipStep}
          </Button>
        )
      }
      {props.nextVisible ? (
        <Button
          disabled={props.nextDisabled}
          onClick={props.onFinish}
          type={props.skipable ? "default" : "primary"}
          wide="long"
        >
          {props.skipable ? languages.skipStep : languages.next}
        </Button>
      ) : null}
      {!props.childrenFirst ? props.children : null}
    </Buttons>
  )
}

export const UiInline: React.FC<UiInlineProps> = (props): JSX.Element => (
  <div className={styles.InlineItem}>
    <img className={styles.InlineImage} src={props.imgUrl} alt={props.item.title} />
    <b className={styles.InlineText}>{props.item.title}</b>
  </div>
)
